import { useEffect, useState } from "react";
import TextInput from "../../component/textInput/TextInput";
import { RequiredLabel } from "../../component/requiredLabel/RequiredLabel";
import Dropdown from "../../component/dropdown/Dropdown";
import CustomPhoneInput from "../../component/customPhoneInput/CustomPhoneInput";
import {
  PrimaryButton,
  SecondaryButton,
} from "../../component/buttonComponent/ButtonComponent";
import ImageInput from "../../component/imageInput/ImageInput";
import { Avatar } from "@mui/material";
import { addPlusPhone } from "../../config/utilities/formatPhone";
import { isValidPhoneNumber } from "react-phone-number-input";
import PasswordStrengthBar from "../../component/passwordStrengthBar/PasswordStrengthBar";
import { getImageFirebase } from "../../config/utilities/getImageFirebase";

const driverOptions = [
  { label: "Driver", value: "driver" },
  { label: "Bus Assistant", value: "assistant" },
];

export const DriverForm = ({
  mode,
  existingData,
  onSubmit,
  handleReset,
  loading,
  input,
  setInput,
  error,
  setError,
}) => {
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [initialInput, setInitialInput] = useState(null);
  const [keyUp, setKeyUp] = useState("", false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );

  const handleDriverPasswordStrengthChange = (strength) => {
    setPasswordStrength(strength);
  };

  const isDriverFormValid = () => {
    let isValid = true;
   const nameRegex = /^[A-Za-z0-9\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+$/;
    const newErrors = {
      name: input.name === "" || !nameRegex.test(input.name.trim()),
      type: input.type === "",
      email: input.email === "" || !isValidEmail(input.email.trim()),
      phoneNumber:
        input.phoneNumber === "" ||
        !isValidPhoneNumber(String(input.phoneNumber)),
      password: input.password === "" || passwordStrength < 100,
      profileImage: !input.profileImage,
      preferredName:
        input.preferredName !== "" &&
        !nameRegex.test(input.preferredName.trim()),
    };
    isValid = Object.values(newErrors).every((val) => val === false);
    const isEdited = initialInput
      ? JSON.stringify(initialInput) !== JSON.stringify(input)
      : input.name.trim() !== "" ||
        input.type.trim() !== "" ||
        input.email.trim() !== "" ||
        input.phoneNumber.trim() !== "" ||
        input.password.trim() !== "" ||
        input.profileImage ||
        input.preferredName?.trim() !== "";
    return isValid && isEdited;
  };
  useEffect(() => {
    const nameRegex = /^[A-Za-z0-9\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+$/;
    setError({
      name: input.name !== "" && !nameRegex.test(input.name.trim()),
      type: input.type !== "",
      email: input.email !== "" && !isValidEmail(input.email.trim()),
      phoneNumber:
        input.phoneNumber !== "" &&
        !isValidPhoneNumber(String(input.phoneNumber)),
      profileImage: !input.profileImage,
      preferredName:
        input.preferredName !== "" &&
        !nameRegex.test(input.preferredName.trim()),
    });
  }, [input, passwordStrength]);
  useEffect(() => {
    setIsSubmitDisabled(!isDriverFormValid());
  }, [input, initialInput, passwordStrength]);

  useEffect(() => {
    const handleResize = () => setIsMobileViewport(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fullWidth = "100%";
  const busWidth = isMobileViewport ? fullWidth : "45rem";
  const emailWidth = isMobileViewport ? fullWidth : "20rem";
  const phoneWidth = isMobileViewport ? fullWidth : "21.9rem";
  const dropdownWidth = isMobileViewport ? fullWidth : "46.9rem";
  const dropzoneWidth = isMobileViewport ? fullWidth : "19.6rem";
  const isValidEmail = (email) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  };
  useEffect(() => {
    if ((mode === "edit" || mode === "view") && existingData) {
      const { _id, institutionName, ...formattedData } = existingData;
      const formattedInitialInput = {
        ...formattedData,
        phoneNumber: addPlusPhone(formattedData.phoneNumber || ""),
      };
      setInitialInput(formattedInitialInput);
      setInput((prev) => ({
        name: existingData.name || prev.name || "",
        type: existingData.type || prev.type || "",
        phoneNumber:
          addPlusPhone(existingData.phoneNumber) ||
          addPlusPhone(prev.phoneNumber) ||
          "",
        password: existingData.password || prev.password || "",
        profileImage: existingData.profileImage || prev.profileImage || "",
        status: existingData.status || prev.status || "active",
        email: existingData.email || prev.email || "",
        firebaseUid: existingData.firebaseUid || prev.firebaseUid || "",
        institutionId: existingData.institutionId || prev.institutionId || "",
        preferredName: existingData.preferredName || prev.preferredName || "",
      }));
      getImageFirebase(existingData.profileImage)
        .then((url) => {
          setInput((prev) => ({ ...prev, profileImage: url }));
          setInitialInput((prev) => ({ ...prev, profileImage: url }));
        })
        .catch((error) => {
          console.error("Error fetching image from Firebase:", error);
        });
    } else {
      setInitialInput(input);
    }
  }, [mode, existingData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedInput = { ...input, [name]: value };
    setInput(updatedInput);
  };

  const handleFileUpload = (base64Data) => {
    setInput((prev) => ({
      ...prev,
      profileImage: base64Data,
    }));
  };

  return (
    <>
      <div className="name-form-container">
        <div className="input-label">
          <RequiredLabel>Driver Name</RequiredLabel>
        </div>
        <div className="input-container">
          <TextInput
            placeholder={mode === "view" ? input.name : "Enter Name"}
            setInput={setInput}
            input={mode === "view" ? "" : input}
            name="name"
            disable={mode === "view"}
            type="text"
            error={error.name}
            setError={setError}
            errorMessage="Driver name should only contain letters and spaces"
            keyUp={keyUp}
            setKeyUp={setKeyUp}
            onChangeFunction={handleInputChange}
            width={busWidth}
          />
        </div>
      </div>
      <div className="name-form-container">
        <div className="input-label">Preferred Name</div>
        <div className="input-container">
          <TextInput
            placeholder={
              mode === "view" ? input.preferredName : "Enter Preferred Name"
            }
            setInput={setInput}
            input={mode === "view" ? "" : input}
            name="preferredName"
            disable={mode === "view"}
            type="text"
            error={error.preferredName}
            setError={setError}
            errorMessage="Preferred name should only contain letters and spaces"
            keyUp={keyUp}
            setKeyUp={setKeyUp}
            onChangeFunction={handleInputChange}
            width={busWidth}
          />
        </div>
      </div>
      <div className="trip-type-name-form-container">
        <div className="input-label">
          <RequiredLabel>Driver Type</RequiredLabel>
          <div className="input-description">
            Select whether the person is the Driver or the Assistant. This helps
            define their role during the trip.
          </div>
        </div>
        <div className="input-container">
          <Dropdown
            disabled={mode === "view"}
            options={driverOptions}
            name="type"
            placeholder="Select a type..."
            onChangeFunction={handleInputChange}
            input={input}
            width={dropdownWidth}
          />
        </div>
      </div>
      <div className="name-form-container">
        <div className="input-label">
          <RequiredLabel>Driver Details</RequiredLabel>
          <div className="input-description">
            Enter the driver details such as email and phone number.
          </div>
        </div>
        <div className="input-containers">
          <div className="input-container">
            <TextInput
              label="Email"
              placeholder={
                mode === "view" || mode === "edit" ? input.email : "Enter Email"
              }
              setInput={setInput}
              input={mode === "view" || mode === "edit" ? "" : input}
              name="email"
              disable={mode === "view" || mode === "edit"}
              type="text"
              error={error.email}
              setError={setError}
              errorMessage="Please enter a valid email address"
              keyUp={keyUp}
              setKeyUp={setKeyUp}
              onChangeFunction={handleInputChange}
              width={emailWidth}
            />
          </div>
          <div className="input-container">
            <CustomPhoneInput
              disabled={mode === "view"}
              label="Phone Number"
              value={input.phoneNumber || ""}
              onChange={(value) =>
                handleInputChange({ target: { name: "phoneNumber", value } })
              }
              width={phoneWidth}
              error={error.phoneNumber}
              errorMessage="Enter valid phone number"
            />
          </div>
        </div>
      </div>
      <div className="name-form-container" style={{ paddingBottom: "2rem" }}>
        <div className="input-label">
          <RequiredLabel>Driver Image</RequiredLabel>
          <div className="input-description">
            Upload a clear image of the driver. This will be necessary to verify
            drivers.
          </div>
        </div>
        <div className="input-containers">
          {mode !== "view" && (
            <div className="input-container">
              <ImageInput
                onFileUpload={handleFileUpload}
                label="Upload Image"
                disable={false}
                type="dropzone"
                dropzoneWidth={dropzoneWidth}
              />
            </div>
          )}
          <div className="input-container driver-image-avatar-container">
            <Avatar
              src={input.profileImage || undefined}
              alt="Driver Photo"
              sx={{ width: "9rem", height: "9rem" }}
            />
          </div>
        </div>
      </div>
      <div className="name-form-container">
        <div className="input-label">
          <RequiredLabel>Password</RequiredLabel>
          <div className="input-description">
            Enter the password which the driver would use to log into the driver
            app.
          </div>
        </div>
        <div className="input-container">
          <TextInput
            placeholder={mode === "view" ? "********" : "Enter Password"}
            setInput={setInput}
            input={mode === "view" ? "" : input}
            name="password"
            disable={mode === "view"}
            type="password"
            error={error.password}
            setError={setError}
            errorMessage=""
            keyUp={keyUp}
            setKeyUp={setKeyUp}
            onChangeFunction={handleInputChange}
            width={busWidth}
          />
          {mode !== "view" && (
            <div
              className="password-strength-bar"
              style={{ marginTop: "-2rem", paddingBottom: "2rem" }}
            >
              <PasswordStrengthBar
                module="drivers"
                password={input.password}
                onStrengthChange={handleDriverPasswordStrengthChange}
              />
            </div>
          )}
        </div>
      </div>
      <div className="button-container-edit">
        <SecondaryButton
          width="6rem"
          label="Cancel"
          imageIcon={false}
          onClickFunction={handleReset}
        />
        {mode !== "view" && (
          <PrimaryButton
            width="6rem"
            disable={isSubmitDisabled}
            label={mode === "create" ? "Create" : "Save"}
            onClickFunction={onSubmit}
            loading={loading}
          />
        )}
      </div>
    </>
  );
};
